import { z } from 'zod';
import {
  CommissionPaymentMethod,
  CommissionPayoutRecipientType,
  CommissionPayoutStatus,
  CommissionSplitMethod,
  EmployeeOverrideType,
} from '../enums/commission.enums';

// ============================================================================
// COMMISSION SPLIT DTOs (Issue #390 - Employee Commission Split)
// ============================================================================
// These schemas define the API request/response formats for commission splits.
// Supports splitting commissions between partner employees and partner companies.

// ============================================================================
// Partner Commission Split Configuration
// ============================================================================

/**
 * Schema for creating a partner commission split configuration
 */
export const CreatePartnerCommissionSplitSchema = z.object({
  partnerId: z.string().uuid(),
  splitMethod: z.nativeEnum(CommissionSplitMethod).default(CommissionSplitMethod.PERCENTAGE),

  // Percentage-based split (when splitMethod = PERCENTAGE)
  employeeSplitPercent: z.number().min(0).max(100).optional(),
  companySplitPercent: z.number().min(0).max(100).optional(),

  // Fixed amount split (when splitMethod = FIXED_EMPLOYEE or FIXED_COMPANY)
  employeeFixedAmount: z.number().min(0).optional(),
  companyFixedAmount: z.number().min(0).optional(),

  // Rate-based split (when splitMethod = RATE_BASED)
  employeeRate: z.number().min(0).max(100).optional(),
  companyRate: z.number().min(0).max(100).optional(),

  // Tiered configuration (when splitMethod = TIERED)
  tiers: z
    .array(
      z.object({
        minVolume: z.number().min(0),
        maxVolume: z.number().min(0).optional(),
        employeePercent: z.number().min(0).max(100),
        companyPercent: z.number().min(0).max(100),
      }),
    )
    .optional(),

  // Minimum thresholds
  minimumEmployeePayout: z.number().min(0).optional(),
  minimumCompanyPayout: z.number().min(0).optional(),

  // Vesting configuration
  vestingEnabled: z.boolean().default(false),
  vestingSchedule: z
    .array(
      z.object({
        days: z.number().int().positive(),
        percent: z.number().min(0).max(100),
      }),
    )
    .optional(),

  // Validity period
  effectiveDate: z.string().datetime().optional(),
  expirationDate: z.string().datetime().optional(),

  notes: z.string().max(1000).optional(),
});

export type CreatePartnerCommissionSplitInput = z.infer<typeof CreatePartnerCommissionSplitSchema>;

/**
 * Schema for updating a partner commission split configuration
 */
export const UpdatePartnerCommissionSplitSchema = CreatePartnerCommissionSplitSchema.partial()
  .omit({ partnerId: true })
  .extend({
    isActive: z.boolean().optional(),
  });

export type UpdatePartnerCommissionSplitInput = z.infer<typeof UpdatePartnerCommissionSplitSchema>;

/**
 * Response schema for partner commission split
 */
export const PartnerCommissionSplitResponseSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  partnerId: z.string().uuid(),
  splitMethod: z.nativeEnum(CommissionSplitMethod),
  employeeSplitPercent: z.number().nullable(),
  companySplitPercent: z.number().nullable(),
  employeeFixedAmount: z.number().nullable(),
  companyFixedAmount: z.number().nullable(),
  employeeRate: z.number().nullable(),
  companyRate: z.number().nullable(),
  tiers: z
    .array(
      z.object({
        minVolume: z.number(),
        maxVolume: z.number().nullable(),
        employeePercent: z.number(),
        companyPercent: z.number(),
      }),
    )
    .nullable(),
  minimumEmployeePayout: z.number().nullable(),
  minimumCompanyPayout: z.number().nullable(),
  vestingEnabled: z.boolean(),
  vestingSchedule: z
    .array(
      z.object({
        days: z.number(),
        percent: z.number(),
      }),
    )
    .nullable(),
  effectiveDate: z.string().datetime(),
  expirationDate: z.string().datetime().nullable(),
  isActive: z.boolean(),
  notes: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PartnerCommissionSplitResponse = z.infer<typeof PartnerCommissionSplitResponseSchema>;

// ============================================================================
// Employee Commission Override
// ============================================================================

/**
 * Schema for creating an employee commission override
 */
export const CreateEmployeeCommissionOverrideSchema = z.object({
  partnerId: z.string().uuid(),
  employeeId: z.string().uuid(),
  employeeName: z.string().min(1).max(255),
  overrideType: z.nativeEnum(EmployeeOverrideType).default(EmployeeOverrideType.RATE),

  // Override values (one of these based on overrideType)
  overrideRate: z.number().min(0).max(100).optional(),
  overridePercent: z.number().min(0).max(100).optional(),
  fixedAmount: z.number().min(0).optional(),

  // Validity period
  effectiveDate: z.string().datetime().optional(),
  expirationDate: z.string().datetime().optional(),

  reason: z.string().max(1000).optional(),
});

export type CreateEmployeeCommissionOverrideInput = z.infer<typeof CreateEmployeeCommissionOverrideSchema>;

/**
 * Schema for updating an employee commission override
 */
export const UpdateEmployeeCommissionOverrideSchema = CreateEmployeeCommissionOverrideSchema.partial()
  .omit({ partnerId: true, employeeId: true })
  .extend({
    isActive: z.boolean().optional(),
  });

export type UpdateEmployeeCommissionOverrideInput = z.infer<typeof UpdateEmployeeCommissionOverrideSchema>;

/**
 * Response schema for employee commission override
 */
export const EmployeeCommissionOverrideResponseSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  partnerId: z.string().uuid(),
  employeeId: z.string().uuid(),
  employeeName: z.string(),
  overrideType: z.nativeEnum(EmployeeOverrideType),
  overrideRate: z.number().nullable(),
  overridePercent: z.number().nullable(),
  fixedAmount: z.number().nullable(),
  effectiveDate: z.string().datetime(),
  expirationDate: z.string().datetime().nullable(),
  isActive: z.boolean(),
  reason: z.string().nullable(),
  createdBy: z.string(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type EmployeeCommissionOverrideResponse = z.infer<typeof EmployeeCommissionOverrideResponseSchema>;

// ============================================================================
// Commission Payout
// ============================================================================

/**
 * Schema for creating a commission payout
 */
export const CreateCommissionPayoutSchema = z.object({
  commissionId: z.string().uuid(),
  recipientType: z.nativeEnum(CommissionPayoutRecipientType),
  recipientId: z.string().uuid(),
  recipientName: z.string().min(1).max(255),
  recipientEmail: z.string().email().optional(),
  recipientPhone: z.string().max(20).optional(),
  rate: z.number().min(0).max(100),
  amount: z.number().min(0),
  splitPercent: z.number().min(0).max(100).optional(),
  notes: z.string().max(1000).optional(),
});

export type CreateCommissionPayoutInput = z.infer<typeof CreateCommissionPayoutSchema>;

/**
 * Schema for updating a commission payout status
 */
export const UpdateCommissionPayoutSchema = z.object({
  status: z.nativeEnum(CommissionPayoutStatus).optional(),
  paymentMethod: z.nativeEnum(CommissionPaymentMethod).optional(),
  paymentRef: z.string().max(100).optional(),
  paymentBatch: z.string().max(100).optional(),
  notes: z.string().max(1000).optional(),
});

export type UpdateCommissionPayoutInput = z.infer<typeof UpdateCommissionPayoutSchema>;

/**
 * Schema for approving a commission payout
 */
export const ApproveCommissionPayoutSchema = z.object({
  notes: z.string().max(1000).optional(),
});

export type ApproveCommissionPayoutInput = z.infer<typeof ApproveCommissionPayoutSchema>;

/**
 * Schema for marking a commission payout as paid
 */
export const MarkCommissionPayoutPaidSchema = z.object({
  paymentMethod: z.nativeEnum(CommissionPaymentMethod),
  paymentRef: z.string().max(100).optional(),
  paymentBatch: z.string().max(100).optional(),
  notes: z.string().max(1000).optional(),
});

export type MarkCommissionPayoutPaidInput = z.infer<typeof MarkCommissionPayoutPaidSchema>;

/**
 * Schema for clawback on a commission payout
 */
export const ClawbackCommissionPayoutSchema = z.object({
  clawbackAmount: z.number().min(0),
  clawbackReason: z.string().min(1).max(1000),
});

export type ClawbackCommissionPayoutInput = z.infer<typeof ClawbackCommissionPayoutSchema>;

/**
 * Response schema for commission payout
 */
export const CommissionPayoutResponseSchema = z.object({
  id: z.string().uuid(),
  commissionId: z.string().uuid(),
  recipientType: z.nativeEnum(CommissionPayoutRecipientType),
  recipientId: z.string().uuid(),
  recipientName: z.string(),
  recipientEmail: z.string().nullable(),
  recipientPhone: z.string().nullable(),
  rate: z.number(),
  amount: z.number(),
  splitPercent: z.number().nullable(),
  status: z.nativeEnum(CommissionPayoutStatus),
  approvedAt: z.string().datetime().nullable(),
  approvedBy: z.string().nullable(),
  paidAt: z.string().datetime().nullable(),
  paymentMethod: z.nativeEnum(CommissionPaymentMethod).nullable(),
  paymentRef: z.string().nullable(),
  paymentBatch: z.string().nullable(),
  clawbackAmount: z.number().nullable(),
  clawbackReason: z.string().nullable(),
  clawbackAt: z.string().datetime().nullable(),
  clawbackBy: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type CommissionPayoutResponse = z.infer<typeof CommissionPayoutResponseSchema>;

// ============================================================================
// Commission with Employee Split
// ============================================================================

/**
 * Schema for creating a commission with employee split
 * Extends the base commission creation with employee tracking
 */
export const CreateCommissionWithSplitSchema = z.object({
  partnerId: z.string().uuid(),
  caseId: z.string().uuid().optional(),
  referralId: z.string().uuid().optional(),

  // Total commission (or individual amounts if manually specified)
  amount: z.number().min(0),
  rate: z.number().min(0).max(100),

  // Employee tracking (Issue #390)
  referringEmployeeId: z.string().uuid().optional(),
  referringEmployeeName: z.string().max(255).optional(),
  referringEmployeeEmail: z.string().email().optional(),

  // Split amounts (if provided, overrides calculation from split config)
  employeeRate: z.number().min(0).max(100).optional(),
  companyRate: z.number().min(0).max(100).optional(),
  employeeAmount: z.number().min(0).optional(),
  companyAmount: z.number().min(0).optional(),

  description: z.string().max(1000).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type CreateCommissionWithSplitInput = z.infer<typeof CreateCommissionWithSplitSchema>;

/**
 * Response schema for commission with split details
 */
export const CommissionWithSplitResponseSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  partnerId: z.string().uuid(),
  caseId: z.string().uuid().nullable(),
  referralId: z.string().uuid().nullable(),
  amount: z.number(),
  rate: z.number(),
  status: z.string(),
  paidAt: z.string().datetime().nullable(),
  paymentMethod: z.string().nullable(),
  description: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),

  // Employee tracking (Issue #390)
  referringEmployeeId: z.string().uuid().nullable(),
  referringEmployeeName: z.string().nullable(),
  referringEmployeeEmail: z.string().nullable(),

  // Split details (Issue #390)
  employeeRate: z.number().nullable(),
  companyRate: z.number().nullable(),
  employeeAmount: z.number().nullable(),
  companyAmount: z.number().nullable(),
  employeePaidAt: z.string().datetime().nullable(),
  companyPaidAt: z.string().datetime().nullable(),
  employeePaymentMethod: z.string().nullable(),
  companyPaymentMethod: z.string().nullable(),
  employeePaymentRef: z.string().nullable(),
  companyPaymentRef: z.string().nullable(),

  // Related payouts
  payouts: z.array(CommissionPayoutResponseSchema).optional(),
});

export type CommissionWithSplitResponse = z.infer<typeof CommissionWithSplitResponseSchema>;

// ============================================================================
// Commission Split Calculation
// ============================================================================

/**
 * Input for calculating commission split
 */
export const CalculateCommissionSplitSchema = z.object({
  partnerId: z.string().uuid(),
  employeeId: z.string().uuid().optional(),
  loanAmount: z.number().min(0),
  totalCommissionRate: z.number().min(0).max(100).optional(), // If not provided, uses partner's default rate
  totalCommissionAmount: z.number().min(0).optional(), // If not provided, calculated from rate
});

export type CalculateCommissionSplitInput = z.infer<typeof CalculateCommissionSplitSchema>;

/**
 * Result of commission split calculation
 */
export const CommissionSplitCalculationResultSchema = z.object({
  totalAmount: z.number(),
  totalRate: z.number(),
  employeeAmount: z.number(),
  employeeRate: z.number(),
  companyAmount: z.number(),
  companyRate: z.number(),
  splitMethod: z.nativeEnum(CommissionSplitMethod),
  splitConfigId: z.string().uuid().nullable(),
  overrideId: z.string().uuid().nullable(),
  vestingApplied: z.boolean(),
  vestedPercent: z.number().nullable(),
});

export type CommissionSplitCalculationResult = z.infer<typeof CommissionSplitCalculationResultSchema>;
